/* eslint-disable react/require-default-props */
/* eslint-disable max-len */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useFormController from '../hooks/useFormController';
import formControllerState from '../utils/formControllerState';
import { ArrowDown } from '../private/icons/ArrowDown';
import useControlled from '../hooks/useControlled';

const NativeDropdown = (props) => {
  const {
    className,
    disabled,
    children,
    value: valueProp,
    defaultValue,
    onChange: onChangeProp,
    placeholder,
    ...rest
  } = props;

  const [value, setValue] = useControlled({
    controlled: valueProp,
    defaultValue
  });

  const onChangeHandler = (event) => {
    if (event.nativeEvent.defaultPrevented) {
      return;
    }
    const newValue = event.target.value;
    setValue(newValue);
    if (onChangeProp) {
      onChangeProp(newValue, event);
    }
  };

  const formControl = useFormController();
  const fcs = formControllerState({
    props,
    formControl,
    states: ['disabled', 'required', 'status']
  });

  const selectClasses = classNames(
    `sui-peer sui-appearance-none focus:sui-bg-input-focus focus:sui-outline focus:sui-outline-4 focus:sui-outline-focus
    sui-px-3 sui-py-2 sui-pr-10 sui-font-regular sui-text-base sui-leading-tight sui-h-10 sui-text-primary sui-w-full sui-rounded-base sui-border-solid sui-border-1`,
    {
      'sui-bg-button-inactive-primary sui-border-primary sui-opacity-100': fcs.disabled,
      'sui-border-strong': !fcs.disabled,
      'hover:sui-border-hover': !fcs.disabled && !fcs.status,
      'sui-border-warning-strong': fcs.status === 'warning',
      'sui-border-danger-strong': fcs.status === 'error',
      'sui-border-success-strong': fcs.status === 'success'
    },
    className,
  );

  const iconSpanClasses = classNames({
    'sui-text-primary peer-hover:sui-text-brand': !fcs.disabled,
    'sui-text-inactive': fcs.disabled,
  });

  const iconClasses = classNames('sui-ml-2 sui-absolute sui-pointer-events-none sui-top-3 sui-right-3 sui-mr-1px');

  const childrenArray = React.Children.toArray(children);
  const items = childrenArray.map((child) => {
    if (!React.isValidElement(child)) {
      return null;
    }
    return (
      <option value={child.props.value} key={child.props.value} disabled={child.props.disabled}>
        {child.props.children}
      </option>
    );
  });

  if (placeholder && (typeof value === 'undefined' || value === null)) {
    items.unshift(<option value="" key="" hidden selected disabled>{placeholder}</option>);
  }

  return (
    <div className="sui-relative sui-w-full">
      <select
        disabled={fcs.disabled}
        className={selectClasses}
        type="button"
        value={value}
        onChange={onChangeHandler}
        {...rest}
      >
        {items}
      </select>
      <span className={iconSpanClasses}>
        <ArrowDown className={iconClasses} size="small" />
      </span>
    </div>
  );
};

NativeDropdown.displayName = 'NativeDropdown';

NativeDropdown.propTypes = {
  /**
   * A list of "ListItem" elements to populate the dropdown with.
   */
  children: PropTypes.node.isRequired,
  /**
   * Additional css class names to be added.
   */
  className: PropTypes.string,
  /**
   * Text to show when no value is selected.
   */
  placeholder: PropTypes.string,
  /**
   * Event fired when the user clicks the input field to open or close the menu list.
   */
  onChange: PropTypes.func,
  /**
   * If `true`, the component is inactive
   */
  disabled: PropTypes.bool,
  /**
   * The value of the selected list item
   */
  value: PropTypes.node,
  /**
   * The default value for the NativeDropdown element. Use when the component is not controlled.
   */
  defaultValue: PropTypes.node,
};

NativeDropdown.defaultProps = {
  className: null,
  onChange: null,
  disabled: undefined,
  placeholder: 'Select an Option',
  value: undefined,
};

export { NativeDropdown };